import React from "react";
import { Button } from "@/components/ui/button";
import { Mail, UserCheck, AlertTriangle } from "lucide-react";
import { WAF_COUNTRY_AMS } from "@/data/waf-region";
import { toast } from "sonner";

const fmtUSD = (v) =>
  typeof v === "number"
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v)
    : v;

/**
 * "Envoyer aux équipes Fortinet" panel.
 *
 * Auto-activated as soon as `customerInfo.country` is filled — there is no
 * toggle anymore (per product request: l'envoi vers les Account Managers
 * doit s'activer automatiquement).
 *
 * The Outlook draft now includes the full customer/partner context captured
 * up-front in the Configurator (siège, agence, partenaire intégrateur, auteur
 * du BOM/BOQ) plus a compact BOM summary and the price/savings breakdown.
 */
export default function EmailQuoteToTeam({ result, customerInfo }) {
  const c = customerInfo || {};
  const team = (c.country && WAF_COUNTRY_AMS[c.country]) || [];
  const accountManagers = team.filter((m) => /Account Manager/i.test(m.role));
  const sysEngineers = team.filter((m) => /System Engineer|Presales/i.test(m.role));
  const allRecipients = [...accountManagers, ...sysEngineers];
  const ready = !!(c.country && allRecipients.length > 0);

  const buildMailto = () => {
    const to = accountManagers.map((m) => m.email).join(",");
    const cc = sysEngineers.map((m) => m.email).join(",");

    const subject =
      `[Pythagore] BOM Fortinet — ${c.name || "Client à préciser"} (${c.country})`;

    const bomLines = (result?.bom || [])
      .slice(0, 14)
      .map((b) => `  • ${b.quantity}× ${b.sku} — ${b.name || ""}`.slice(0, 110));
    const moreCount = Math.max(0, (result?.bom?.length || 0) - 14);

    const inputs = result?.inputs || {};
    const summary = result?.architecture_summary || "";
    const total = result?.total_cost ?? 0;
    const list = result?.total_list_cost ?? null;
    const savings = result?.total_savings ?? null;
    const savingsPct =
      typeof list === "number" && list > 0 && typeof savings === "number"
        ? ((savings / list) * 100).toFixed(2)
        : null;
    const tier = result?.partner_level;

    const sec = (label, value) =>
      value ? `${label.padEnd(22, " ")}: ${value}` : null;

    const body = [
      `Bonjour,`,
      ``,
      `Je vous transmets pour revue le BOM/BOQ Pythagore concernant le client ci-dessous.`,
      ``,
      `─── CLIENT ──────────────────────────────────────`,
      sec("Nom du client", c.name),
      sec("Pays", c.country),
      sec("Siège", c.headquarters),
      sec("Agence(s)", c.branch),
      ``,
      `─── PARTENAIRE ──────────────────────────────────`,
      sec("Partenaire", c.partner_name),
      sec("BOM/BOQ généré par", c.generated_by),
      sec("E-mail auteur", c.generated_by_email),
      ``,
      `─── PROJET ──────────────────────────────────────`,
      sec("Type client", inputs.customer_type),
      sec("Cas d'usage", inputs.use_case),
      sec("Niveau de sécurité", inputs.security_level),
      sec("Utilisateurs", inputs.users),
      sec("Sites", inputs.sites),
      sec("Durée licence", inputs.license_years ? `${inputs.license_years} an(s)` : null),
      ``,
      `─── ARCHITECTURE ────────────────────────────────`,
      summary,
      ``,
      `─── COMPOSITION (${result?.bom?.length || 0} ligne${(result?.bom?.length || 0) > 1 ? "s" : ""}) ─`,
      ...bomLines,
      moreCount > 0 ? `  … et ${moreCount} ligne(s) supplémentaire(s) — voir export PDF/Excel attaché.` : "",
      ``,
      `─── TARIFICATION ────────────────────────────────`,
      list != null ? sec("Total liste public", fmtUSD(list)) : null,
      tier && savings ? sec(`Remise partenaire (${tier})`, `-${fmtUSD(savings)}${savingsPct ? ` (-${savingsPct}%)` : ""}`) : null,
      sec("Total NET partenaire", fmtUSD(total)),
      ``,
      `Pourriez-vous valider la sizing et confirmer la disponibilité avant remise officielle au client ?`,
      ``,
      `Cordialement,`,
      `${c.generated_by || "Partenaire Fortinet"}${c.partner_name ? ` — ${c.partner_name}` : ""}`,
      `(Brouillon généré via Pythagore)`,
    ].filter(Boolean).join("\r\n");

    return `mailto:${encodeURIComponent(to)}?cc=${encodeURIComponent(cc)}` +
      `&subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;
  };

  const onSend = () => {
    if (!ready) {
      toast.error("Renseignez le nom et le pays du client en haut du configurateur.");
      return;
    }
    window.location.href = buildMailto();
    toast.success(
      `E-mail prêt pour ${accountManagers.length} AM + ${sysEngineers.length} SE — ${c.country}.`
    );
  };

  // No country yet → minimal hint, never block the BOM rendering.
  if (!c.country) {
    return (
      <div className="border border-zinc-200 bg-white rounded-sm" data-testid="email-quote-to-team-empty">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
          <Mail className="h-4 w-4 text-zinc-400" />
          <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500">
            Envoyer aux équipes Fortinet
          </h3>
        </div>
        <div className="px-5 py-4 flex items-start gap-2 text-xs text-zinc-500">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
          <p>
            Renseignez le pays du client dans la section <strong>Informations client</strong> en haut du configurateur pour activer automatiquement l'envoi vers les Account Managers + System Engineers du pays.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-zinc-200 bg-white rounded-sm" data-testid="email-quote-to-team">
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-[#D42B1E]" />
          <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500">
            Envoyer aux équipes Fortinet — {c.country}
          </h3>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 text-[10px] font-bold tracking-wider">
          ACTIVÉ
        </span>
      </div>

      <div className="px-5 py-4 space-y-3">
        {allRecipients.length === 0 ? (
          <p className="text-xs text-zinc-500">Aucun contact référencé pour ce pays.</p>
        ) : (
          <ul className="space-y-1.5 text-sm" data-testid="email-team-recipients">
            {accountManagers.map((m) => (
              <li key={m.email} className="flex items-center gap-2 text-zinc-800">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#D42B1E]/10 text-[#D42B1E] text-[10px] font-bold tracking-wider">
                  <UserCheck className="h-3 w-3" /> AM
                </span>
                <span className="font-medium">{m.name}</span>
                <span className="text-xs text-zinc-500">· {m.role}</span>
                <span className="ml-auto font-mono text-[11px] text-zinc-500">{m.email}</span>
              </li>
            ))}
            {sysEngineers.map((m) => (
              <li key={m.email} className="flex items-center gap-2 text-zinc-800">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-sky-100 text-sky-700 text-[10px] font-bold tracking-wider">
                  SE
                </span>
                <span className="font-medium">{m.name}</span>
                <span className="text-xs text-zinc-500">· {m.role}</span>
                <span className="ml-auto font-mono text-[11px] text-zinc-500">{m.email}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="pt-1 flex items-center justify-end gap-2">
          <Button
            onClick={onSend}
            disabled={!ready}
            className="rounded-sm bg-[#D42B1E] hover:bg-[#B22318] text-white"
            data-testid="email-team-send"
          >
            <Mail className="h-4 w-4 mr-2" />
            Envoyer par mail
          </Button>
        </div>
      </div>
    </div>
  );
}
